/**
 * PanelListItem — the ONE row for PanelList content (TD-004). Slots cover
 * every panel list: leading (icon/avatar chip), title, meta (badge row),
 * preview (clamped content), trailing (actions/date — clicks there never
 * open the row). Pressing the row opens a view-mode modal with the full
 * content (`detail`), or runs `onPress` for navigation rows.
 */

import React, { ReactNode, useState } from "react";
import { cn } from "@/utils/cn";
import { Modal } from "@/components/ui/Modal";
import { ChevronRightIcon } from "@/components/icons";

interface PanelListItemProps {
  /** Small icon/avatar chip on the left. */
  leading?: ReactNode;
  /** Main line. Optional for content-first rows (notes). */
  title?: ReactNode;
  /** Badge/label row under the title. */
  meta?: ReactNode;
  /** Clamped content preview under the meta row. */
  preview?: ReactNode;
  /** Right cluster: actions, timestamps, chevrons. Click-isolated. */
  trailing?: ReactNode;
  /** Full content for the view-mode modal opened by pressing the row. */
  detail?: ReactNode;
  detailTitle?: string;
  /** Called when the detail modal opens (lazy-load detail data). */
  onDetailOpen?: () => void;
  /** Navigation rows: overrides the detail modal. */
  onPress?: () => void;
  className?: string;
}

export const PanelListItem: React.FC<PanelListItemProps> = ({
  leading,
  title,
  meta,
  preview,
  trailing,
  detail,
  detailTitle,
  onDetailOpen,
  onPress,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const pressable = Boolean(onPress || detail);

  const handlePress = () => {
    if (onPress) {
      onPress();
      return;
    }
    if (detail) {
      onDetailOpen?.();
      setOpen(true);
    }
  };

  return (
    <>
      <div
        role={pressable ? "button" : undefined}
        tabIndex={pressable ? 0 : undefined}
        onClick={pressable ? handlePress : undefined}
        onKeyDown={
          pressable
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handlePress();
                }
              }
            : undefined
        }
        className={cn(
          "group flex items-center gap-3 py-2",
          pressable &&
            "cursor-pointer rounded-md px-1 -mx-1 hover:bg-gray-50/60 transition-colors",
          className
        )}
      >
        {leading && <div className="shrink-0">{leading}</div>}
        <div className="min-w-0 flex-1">
          {title && (
            <div className="text-sm font-semibold text-gray-900 truncate">
              {title}
            </div>
          )}
          {meta && (
            <div className={cn("flex items-center gap-2 flex-wrap", title && "mt-1")}>
              {meta}
            </div>
          )}
          {preview && (
            <div className="mt-1 text-sm text-gray-600 line-clamp-2 whitespace-pre-wrap">
              {preview}
            </div>
          )}
        </div>
        {trailing && (
          <div
            className="shrink-0 flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {trailing}
          </div>
        )}
        {onPress && (
          <ChevronRightIcon className="w-4 h-4 text-gray-400 group-hover:text-gray-600 shrink-0" />
        )}
      </div>

      {detail && (
        <Modal
          isOpen={open}
          onClose={() => setOpen(false)}
          title={detailTitle ?? "Details"}
          size="lg"
          bodyPadding
        >
          {detail}
        </Modal>
      )}
    </>
  );
};
